import React, { useState } from "react";
import {
  TableCell,
  TableCellLayout,
  TableRow,
  TableRowData as RowStateBase,
} from "@fluentui/react-components/unstable";
import { IExpectedHoursItem } from "../../services/types/expectedHours";
import {
  Button,
  Dialog,
  DialogActions,
  DialogBody,
  DialogContent,
  DialogSurface,
  DialogTitle,
  DialogTrigger,
  Menu,
  MenuItem,
  MenuList,
  MenuPopover,
  MenuTrigger,
  Textarea,
} from "@fluentui/react-components";
import { MoreHorizontalRegular } from "@fluentui/react-icons";
import { AppConfirmdialog } from "../common";
import { ListChildComponentProps } from "react-window";
import styles from "./ExpectedHoursRowStyles";
import { getContractTypeLabel } from "../../utils/contractType";

interface ReactWindowRenderFnProps extends ListChildComponentProps {
  data: TableRowData[];
  acceptSettlement: (item: IExpectedHoursItem) => Promise<void>;
  rejectSettlement: (item: IExpectedHoursItem, rejectionReason: string) => Promise<void>;
  canManageActions: boolean;
}

interface TableRowData extends RowStateBase<IExpectedHoursItem> { }

function baselineStaleHintTitle(item: IExpectedHoursItem): string | undefined {
  if (!item.baselineDataChangeReason) return undefined;
  return item.baselineDataChangedAtUtc
    ? `${item.baselineDataChangeReason} (${item.baselineDataChangedAtUtc})`
    : item.baselineDataChangeReason;
}

const ExpectedHoursRow: React.FC<ReactWindowRenderFnProps> = ({
  data,
  index,
  style,
  acceptSettlement,
  rejectSettlement,
  canManageActions
}) => {
  const [openAcceptDialog, setOpenAcceptDialog] = useState(false);
  const [openRejectDialog, setOpenRejectDialog] = useState(false);
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [rejectionReason, setRejectionReason] = useState("");
  const [rejectValidationError, setRejectValidationError] = useState("");

  const row = data[index];
  const item = row.item;
  const rowStyle = item.isValuesConsistent ? styles.rowConsistent : styles.rowInconsistent;

  const resetRejectDialogState = () => {
    setRejectionReason("");
    setRejectValidationError("");
  };

  const handleOpenRejectDialog = (event: React.MouseEvent) => {
    event.stopPropagation();
    resetRejectDialogState();
    setOpenRejectDialog(true);
  };

  const handleOpenAcceptDialog = (event: React.MouseEvent) => {
    event.stopPropagation();
    setOpenAcceptDialog(true);
  };

  const handleAccept = async () => {
    setIsSubmitting(true);
    try {
      await acceptSettlement(item);
      setOpenAcceptDialog(false);
    } finally {
      setIsSubmitting(false);
    }
  };

  const handleReject = async () => {
    const trimmedReason = rejectionReason.trim();
    if (!trimmedReason) {
      setRejectValidationError("Powód odrzucenia jest wymagany.");
      return;
    }

    setRejectValidationError("");
    setIsSubmitting(true);
    try {
      await rejectSettlement(item, trimmedReason);
      setOpenRejectDialog(false);
      resetRejectDialogState();
    } finally {
      setIsSubmitting(false);
    }
  };

  const statusLabel =
    item.settlementStatus === "accepted"
      ? "Zaakceptowano"
      : item.settlementStatus === "rejected"
        ? "Odrzucono"
        : null;

  const getStatusStyle = () => {
    if (item.settlementStatus === "accepted") return styles.statusLabelAccepted;
    if (item.settlementStatus === "rejected") return styles.statusLabelRejected;
    return undefined;
  };

  const renderActions = () => {
    const showBaselineStaleHint =
      item.baselineDataChangedAfterDecision && item.settlementStatus;

    const baselineHint = showBaselineStaleHint ? (
      <span style={styles.baselineStaleHint} title={baselineStaleHintTitle(item)}>
        Zmiana danych po decyzji
      </span>
    ) : null;

    if (!canManageActions) {
      return (
        <div style={styles.actionsStack}>
          <div style={styles.actionsTopRow}>
            <span style={getStatusStyle()}>{statusLabel ?? "-"}</span>
          </div>
          {baselineHint}
        </div>
      );
    }

    return (
      <div style={styles.actionsStack}>
        <div style={styles.actionsTopRow}>
          {statusLabel && (
            <span style={{ ...getStatusStyle(), ...styles.statusLabelInline }}>
              {statusLabel}
            </span>
          )}
          <Menu>
            <MenuTrigger disableButtonEnhancement>
              <Button
                size="small"
                appearance="outline"
                icon={<MoreHorizontalRegular />}
                disabled={isSubmitting}
                aria-label="Akcje"
              />
            </MenuTrigger>
            <MenuPopover>
              <MenuList>
                {item.settlementStatus !== "accepted" && (
                  <MenuItem onClick={handleOpenAcceptDialog}>Zaakceptuj</MenuItem>
                )}
                {item.settlementStatus !== "rejected" && (
                  <MenuItem onClick={handleOpenRejectDialog}>Odrzuć</MenuItem>
                )}
              </MenuList>
            </MenuPopover>
          </Menu>
        </div>
        {baselineHint}
      </div>
    );
  };

  return (
    <>
      <TableRow
        style={{
          ...style,
          ...rowStyle,
        }}
      >
        <TableCell>{item.userName}</TableCell>
        <TableCell>{getContractTypeLabel(item.contractType)}</TableCell>
        <TableCell>{item.youtrack}</TableCell>
        <TableCell>{item.absentify}</TableCell>
        <TableCell>{item.toCollect}</TableCell>
        <TableCell>{item.collected}</TableCell>
        <TableCell>{item.timePaidout100}</TableCell>
        <TableCell>{item.timePaidout150}</TableCell>
        <TableCell>{item.timePaidout200}</TableCell>
        <TableCell>{item.absentifyIgnored}</TableCell>
        <TableCell>{item.total}</TableCell>
        <TableCell>{item.workingHours}</TableCell>
        <TableCell>{item.target}</TableCell>
        <TableCell style={styles.actionsCell}>
          <TableCellLayout style={styles.actionsCellLayout}>
            {renderActions()}
          </TableCellLayout>
        </TableCell>
      </TableRow>
      <AppConfirmdialog
        title="Akceptacja rozliczenia"
        bodyTxt={`Czy na pewno chcesz zaakceptować rozliczenie dla ${item.userName}?`}
        setOpen={setOpenAcceptDialog}
        open={openAcceptDialog}
        onAccept={handleAccept}
        isLoading={isSubmitting}
      />
      <Dialog open={openRejectDialog} onOpenChange={(_, dialogData) => setOpenRejectDialog(dialogData.open)}>
        <DialogSurface>
          <DialogBody>
            <DialogTitle>Odrzucenie rozliczenia</DialogTitle>
            <DialogContent>
              Podaj powód odrzucenia dla {item.userName}.
              <div style={styles.dialogContentWrapper}>
                <Textarea
                  style={styles.textarea}
                  value={rejectionReason}
                  onChange={(_, textareaData) => {
                    setRejectionReason(textareaData.value);
                    if (textareaData.value.trim()) {
                      setRejectValidationError("");
                    }
                  }}
                />
                {rejectValidationError && (
                  <div style={styles.validationError}>{rejectValidationError}</div>
                )}
              </div>
            </DialogContent>
            <DialogActions>
              <DialogTrigger disableButtonEnhancement>
                <Button appearance="secondary">Anuluj</Button>
              </DialogTrigger>
              <Button appearance="primary" onClick={handleReject} disabled={isSubmitting}>
                Odrzuć
              </Button>
            </DialogActions>
          </DialogBody>
        </DialogSurface>
      </Dialog>
    </>
  );
};

export default ExpectedHoursRow;
